import type { AppState } from "./state/app-state";
import type { CameraSnapshots } from "@src/features/visualization/camera-snapshots";
import type {
  EquationDocument,
  EvaluationResult,
} from "@src/features/equation-editor/equation";
import type {
  ShareDocument,
  SharedEquation,
} from "@src/features/sharing/codec/share-document";
import type { Dimension, MatrixFor } from "@src/math/types";
import { canVisualizeAnimation } from "./animation-eligibility";
import { copyMatrix } from "@src/math/matrix";
import { createRestoredEquation } from "@src/features/equation-editor/equation-operations";
import {
  createIdlePlayback,
  restorePausedPlayback,
} from "@src/features/playback/playback";
import { parseEquationDocument } from "@src/features/equation-editor/equation-document";
import { parseShareDocument } from "@src/features/sharing/codec/share-document";
import { selectCanShare } from "./state/app-state";

export interface ShareEquationDocuments {
  readonly 2: EquationDocument<2>;
  readonly 3: EquationDocument<3>;
}

export interface EvaluatedEquationDocument<D extends Dimension> {
  readonly document: EquationDocument<D>;
  readonly result: EvaluationResult<D>;
}

export interface EvaluatedShareDocuments {
  readonly 2: EvaluatedEquationDocument<2>;
  readonly 3: EvaluatedEquationDocument<3>;
}

export function captureShareDocument(
  state: AppState,
  elapsedMs: number,
  cameras: CameraSnapshots,
): ShareDocument {
  if (!selectCanShare(state))
    throw new Error("Only a valid workspace can be shared");
  return parseShareDocument({
    activeDimension: state.activeDimension,
    showBasis: state.showBasis,
    showGrid: state.showGrid,
    playback:
      state.animation.status === "idle"
        ? { status: "idle", mode: state.animation.mode }
        : { status: "paused", mode: state.animation.mode, elapsedMs },
    equations: {
      2: captureEquation(state.equations[2], state.appliedTransforms[2]),
      3: captureEquation(state.equations[3], state.appliedTransforms[3]),
    },
    cameras,
  });
}

function captureEquation<D extends Dimension>(
  document: EquationDocument<D>,
  appliedTransform: Readonly<MatrixFor<D>>,
) {
  return {
    matrices: document.matrices.map((matrix) => ({
      label: matrix.label,
      entries: [...matrix.entries],
      durationMs: matrix.durationMs,
    })),
    vectors: document.vectors.map((vector) => ({
      label: vector.label,
      coordinates: [...vector.coordinates],
      color: vector.color,
    })),
    appliedTransform: [...appliedTransform],
  };
}

export function restoreShareEquationDocuments(
  document: ShareDocument,
  createId: () => string,
): ShareEquationDocuments {
  try {
    return {
      2: restoreSharedEquation(2, document.equations[2], createId),
      3: restoreSharedEquation(3, document.equations[3], createId),
    };
  } catch (cause) {
    throw new Error("Invalid share document", { cause });
  }
}

function restoreSharedEquation<D extends Dimension>(
  dimension: D,
  snapshot: SharedEquation<D>,
  createId: () => string,
): EquationDocument<D> {
  return parseEquationDocument(
    {
      dimension,
      matrices: snapshot.matrices.map((matrix) => ({
        id: createId(),
        label: matrix.label,
        entries: matrix.entries,
        durationMs: matrix.durationMs,
      })),
      vectors: snapshot.vectors.map((vector) => ({
        id: createId(),
        label: vector.label,
        coordinates: vector.coordinates,
        color: vector.color,
      })),
    },
    dimension,
  );
}

export function restoreShareWorkspace(
  document: ShareDocument,
  evaluated: EvaluatedShareDocuments,
): AppState {
  const equation2 = restoreEvaluatedEquation(evaluated[2]);
  const equation3 = restoreEvaluatedEquation(evaluated[3]);
  const activeEquation = document.activeDimension === 2 ? equation2 : equation3;
  const animation =
    document.playback.status === "paused" &&
    activeEquation.matrices.length > 0 &&
    canVisualizeAnimation(
      activeEquation.evaluation.value,
      document.playback.mode,
    )
      ? restorePausedPlayback(
          document.playback.mode,
          document.playback.elapsedMs,
        )
      : createIdlePlayback(document.playback.mode);
  return {
    activeDimension: document.activeDimension,
    equations: { 2: equation2, 3: equation3 },
    appliedTransforms: {
      2: copyMatrix(2, document.equations[2].appliedTransform),
      3: copyMatrix(3, document.equations[3].appliedTransform),
    },
    animation,
    showBasis: document.showBasis,
    showGrid: document.showGrid,
  };
}

function restoreEvaluatedEquation<D extends Dimension>({
  document,
  result,
}: EvaluatedEquationDocument<D>) {
  if (result.kind !== "current")
    throw new Error("Invalid share document", {
      cause: new Error("Shared workspace is not valid"),
    });
  return createRestoredEquation(document, result, result.value);
}
